import React, { useEffect, useRef, useState } from "react";
import icons from "../../../../assets/images/constantIcons";
import Table from "../../../Common/Table";
import Pagination from "../../../Common/Pagination";
import useAddGameTable from "./useAddGameTable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import AverageTableHis from "./AverageTableHis";

const GameHistory = () => {
  const {
    columns,
    currentRows,
    totalRows,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    loading,
    selectedFilter,
    setSelectedFilter,
    setFromDate,
    setToDate,
    fromDate,
    toDate, tableHistoryData, tableNames, averageData
  } = useAddGameTable();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [activeTab, setActiveTab] = useState("table");
  const filters = ["Today", "Weekly", "Monthly"];

  const handleFilterChange = (filter) => {
    if (filter === "Custom Date") {
      setIsCustomDateOpen((prev) => !prev);
    } else {
      setSelectedFilter(filter.toLowerCase());
      setIsDropdownOpen(false);
    }
  };

  const applyFilter = () => {
    if (fromDate && toDate) {
      setSelectedFilter("custom");
    }
    setIsDropdownOpen(false);
    setIsCustomDateOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setIsCustomDateOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  const downloadTransactions = async () => {
    if (tableHistoryData.length === 0) {
      toast.warn("No table history available to download.", { duration: 1000 });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Table History");

    const headers = [
      { header: "DATE", key: "date", width: 20 },
      ...tableNames.flatMap((name) => [
        { header: `${name} (₹)`, key: name, width: 20 },
        { header: `${name}_Runtime`, key: `${name}_Runtime`, width: 20 },
      ]),
      { header: "TOTAL COLLECTION", key: "totalCollection", width: 25 },
    ];

    worksheet.columns = headers;

    // Add Header Row
    const headerRow = worksheet.addRow(headers.map((h) => h.header));
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    // Add Average Row
    const avgRowValues = {};
    headers.forEach(({ key }) => {
      if (key === "date") {
        avgRowValues[key] = "TABLE TOTAL";
      } else if (key.endsWith("_Runtime")) {
        avgRowValues[key] = "-";
      } else if (key === "totalCollection") {
        avgRowValues[key] = "-";
      } else {
        const avgEntry = averageData.find((item) => item.table_name === key);
        avgRowValues[key] = avgEntry ? `₹ ${avgEntry.total_amount}` : "-";
      }
    });
    const avgRow = worksheet.addRow(avgRowValues);
    avgRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FF006400" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    // Add Table Data Rows
    tableHistoryData.forEach((row) => {
      const formattedRow = {};
      headers.forEach(({ key }) => {
        formattedRow[key] = row?.[key] ?? "";
      });
      const addedRow = worksheet.addRow(formattedRow);
      addedRow.eachCell((cell) => {
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
    });

    // Adjust row height
    worksheet.eachRow({ includeEmpty: false }, (row) => {
      row.height = 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const now = new Date();
    const date = now.toLocaleDateString("en-GB").replace(/\//g, "-");
    const time = now
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/:/g, "-")
      .replace(/\s/g, "");

    const fileName = `TableHistory_${date}_${time}.xlsx`;
    saveAs(blob, fileName);
  };



  return (
    <div className="px-4 py-4">
      <h1 className="main_heading text-2xl xl:text-[30px] mb-3">Report</h1>

      <div className="flex gap-2 mt-4 mb-2">
        <button
          onClick={() => setActiveTab("table")}
          className={`px-4 py-1 rounded-lg text-sm font-medium border ${activeTab === "table" ? "bg-green text-white border-green" : "bg-white text-G700 border-G300"}`}
        >
          Table History
        </button>
        <button
          onClick={() => setActiveTab("average")}
          className={`px-4 py-1 rounded-lg text-sm font-medium border ${activeTab === "average" ? "bg-green text-white border-green" : "bg-white text-G700 border-G300"}`}
        >
          Table Average
        </button>
      </div>

      <div className="flex flex-col gap-2 lg:gap-0 xs:flex-row justify-between">
        {activeTab === "average" ? (
          <p className="text-G800 font-medium text-xl">Table Average </p>
        ) : (
          <p className="text-G800 font-medium text-xl">Table History</p>
        )}
        {activeTab === "table" && (
          <div className='flex gap-3'>
            <div
              onClick={downloadTransactions}
              className="bg-white text-green rounded-md shadow-sm text-base sm:text-2xl px-2 cursor-pointer flex items-center"
            >
              <icons.FiDownload />
            </div>
            <div className="relative" ref={dropdownRef}>
              <div
                className="flex border items-center text-G100 p-2 rounded-lg font-normal bg-white cursor-pointer"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <icons.GoSearch className="mr-2" />
                <span className="w-36 md:w-[160px] text-sm">
                  {selectedFilter
                    ? selectedFilter.charAt(0).toUpperCase() +
                    selectedFilter.slice(1).toLowerCase()
                    : "Date period search"}
                </span>
                <icons.RiArrowDropDownLine className="ml-2 text-2xl" />
              </div>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-52 md:w-56 bg-white border rounded-lg shadow-lg z-10 p-2">
                  {filters.map((filter) => (
                    <div
                      key={filter}
                      className={`text-sm p-2 rounded-md cursor-pointer hover:bg-green50 ${selectedFilter === filter.toLowerCase()
                        ? "text-green font-semibold"
                        : "text-G500 "
                        }`}
                      onClick={() => handleFilterChange(filter)}
                    >
                      {filter}
                    </div>
                  ))}

                  {/* Custom Date Picker */}
                  <div
                    className="flex justify-between items-center px-2 py-2 hover:bg-green50 rounded-md cursor-pointer"
                    onClick={() => handleFilterChange("Custom Date")}
                  >
                    <span className=" text-sm text-G500 ">Custom Date</span>
                    <icons.MdOutlineCalendarMonth className="text-green700 text-xl" />
                  </div>
                  {isCustomDateOpen && (
                    <div className="p-2">
                      <DatePicker
                        selected={fromDate}
                        onChange={(date) => setFromDate(date)}
                        selectsStart
                        startDate={fromDate}
                        endDate={toDate}
                        placeholderText="From Date"
                        className="border px-3 py-1 rounded w-full mb-2 outline-none text-sm"
                        dateFormat="yyyy-MM-dd"
                      />
                      <DatePicker
                        selected={toDate}
                        onChange={(date) => setToDate(date)}
                        selectsEnd
                        startDate={fromDate}
                        endDate={toDate}
                        minDate={fromDate}
                        placeholderText="To Date"
                        className="border px-3 py-1 rounded w-full outline-none text-sm"
                        dateFormat="yyyy-MM-dd"
                      />

                      {/* Buttons */}
                      <div className="flex justify-between mt-2">
                        <button
                          className="border border-green text-green text-sm px-4 py-1 rounded-md w-[95px]"
                          onClick={() => {
                            setIsDropdownOpen(false);
                            setIsCustomDateOpen(false);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={applyFilter}
                          className="border border-green text-green text-sm px-4 py-1 rounded-md w-[95px]"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                  <div
                    onClick={() => {
                      setSelectedFilter("");
                      setFromDate(null);
                      setToDate(null);
                      setIsDropdownOpen(false);
                      setIsCustomDateOpen(false);
                    }}
                    className="w-full text-sm p-2 rounded-md cursor-pointer hover:bg-green50 text-G500 "
                  >
                    Clear filters
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>


      {activeTab === "average" ? (
        <div className="mb-4">
          <AverageTableHis />
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center my-4">
          <p className="text-green700 font-medium">Loading data...</p>
        </div>
      ) : (
        <div className="my-2 mb-6 overflow-auto horizontalscroll">
          <Table columns={columns} data={currentRows} />
        </div>
      )}
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  );
};

export default GameHistory;
